import { action } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task, type TaskInstance } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type HotelSettingsPreferredRateController from 'lumen/controllers/hotel/settings/preferred-rate';
import type { PaRatePreferenceOption } from 'lumen/models';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { PrimaSettingsInProgress } from 'lumen/services/pricing-assistant-data-request-service';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import getAPIErrorMessage from 'lumen/utils/get-api-error-message';

interface PreferredRateModel {
  launchRatePreferenceSimulateRequestTask: Task<
    { arePreferredRateSettingsValid: boolean | null; isSuccess: boolean },
    [unknown]
  >;
  primaSettingsInProgressTaskResult: TaskInstance<PrimaSettingsInProgress | null>;
  ratePreferenceTaskResult: TaskInstance<void>;
  requestPricingAssistantRatePreferencesOptionsTaskResult: TaskInstance<
    PaRatePreferenceOption | null | undefined
  >;
  saveAndUpdatePreferredRateSettings: Task<{ isSuccess: boolean }, [unknown]>;
}

/**
 * Apparently `isAborted` is a private API of transition so it's not in the types, but we do rely on it.
 * @see https://github.com/emberjs/rfcs/issues/211 (probably similar)
 */
type TransitionWithIsAborted = Transition & { isAborted: boolean };

export default class HotelSettingsPreferredRateRoute extends Route {
  @service declare access: Services['access'];
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;

  @service declare intl: Services['intl'];
  @service declare navigation: Services['navigation'];
  @service
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  @service declare router: Services['router'];
  @service declare store: Services['store'];

  leaveWithoutSaving = false;

  confirmLeaveTask = task(
    { drop: true },
    async (transition: TransitionWithIsAborted) => {
      this.leaveWithoutSaving = await this.alerting.showWarningAlert({
        title: this.intl.t('pricing_assistant.warning_banner.header'),
        dangerMode: true,
        buttons: {
          cancel: {
            text: this.intl.t(
              'pricing_assistant.warning_banner.secondary_button.label',
            ),
          },
          confirm: {
            text: this.intl.t(
              'pricing_assistant.warning_banner.primary_button.label',
            ),
          },
        },
      });

      if (this.leaveWithoutSaving) {
        transition.retry();
      }
    },
  );
  launchRatePreferenceSimulateRequestTask = task(
    { restartable: true },
    async (preferredRateSettings) => {
      let apiResponse = null;

      try {
        const standardParams = {
          hotel_id: this.hotelId,
        };

        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/recommendations/simulate/rate_preferences',
          standardParams,
          this.ajax.host,
        );

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        apiResponse = await this.ajax.post(requestUrl, {
          data: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            pa_rate_preference: preferredRateSettings,
          },
          contentType: 'application/json',
        });

        return {
          isSuccess: true,
          arePreferredRateSettingsValid:
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            apiResponse.carecommendations?.length !== 0,
        };
      } catch (error) {
        this.errorTracking.captureException(error);

        return {
          isSuccess: false,
          arePreferredRateSettingsValid: null,
        };
      }
    },
  );

  requestPricingAssistantRatePreferencesOptionsTask = task(
    { restartable: true },
    async (hotelId) => {
      try {
        const apiResponse =
          await this.store.queryRecord<PaRatePreferenceOption>(
            'pa-rate-preference-option',
            {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              subscription_id: hotelId,
              namespace: APIGATEWAY,
              typeKey: 'pricing-assistant/settings/rate_preferences/options',
            },
          );
        return apiResponse;
      } catch (error) {
        this.basicErrorHandling(error);
      }

      return null;
    },
  );

  saveAndUpdatePreferredRateSettingsTask = task(
    { drop: true },
    async (preferredRateSettings) => {
      let apiResponse = null;

      try {
        const standardParams = {
          subscription_id: this.hotelId,
        };

        const requestUrl = formatUrl(
          '/apigateway/v1/app/pricing-assistant/settings/rate_preferences',
          standardParams,
          this.ajax.host,
        );

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        apiResponse = await this.ajax.put(requestUrl, {
          data: {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            pa_rate_preference: preferredRateSettings,
          },
          contentType: 'application/json',
        });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.hotelAndMarketService.hotel.paRatePreference =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          apiResponse.pa_rate_preference;

        // Updating selected channel can update the status of
        // recommendationsChannels (say we request a supported channel), so we
        // re-request the hotel resource to have the latest channel statuses.
        await this.store.query<Hotel>('hotel', {
          id: this.hotelAndMarketService.hotel.id,
        });

        return {
          isSuccess: true,
        };
      } catch (error) {
        this.basicErrorHandling(error);
        return {
          isSuccess: false,
        };
      }
    },
  );

  get hotelId(): string {
    return this.hotelAndMarketService.hotel.id!;
  }

  activate(transition: Transition): void {
    super.activate(transition);
    // Reset the "leave without saving" state on route activation so the willTransition hook will recheck the unsaved changes
    this.leaveWithoutSaving = false;
  }

  basicErrorHandling(error: unknown): void {
    if (error) {
      this.errorTracking.captureException(error);
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.alerting.showErrorAlert({
      text: getAPIErrorMessage(error, this.intl),
    });
  }

  beforeModel(transition: Transition): void {
    if (this.navigation.redirectToAccountSelection(transition)) {
      return;
    }

    if (!this.access.hasPricingAssistantSettingsPreferredRateTab) {
      this.router.transitionTo('hotel.settings.index');
    }
  }

  model(): PreferredRateModel {
    // The preferred-rate component clones the preference in a field initialiser,
    // so the template gates on this task until it lands.
    const ratePreferenceTaskResult =
      this.pricingAssistantDataRequestService.loadPaRatePreferenceTask.perform(
        this.hotelId,
      );
    const primaSettingsInProgressTaskResult =
      this.pricingAssistantDataRequestService.requestPrimaSettingsInProgressTask.perform(
        this.hotelId,
      );

    const model: PreferredRateModel = {
      requestPricingAssistantRatePreferencesOptionsTaskResult:
        this.requestPricingAssistantRatePreferencesOptionsTask.perform(
          this.hotelId,
        ),
      launchRatePreferenceSimulateRequestTask:
        this.launchRatePreferenceSimulateRequestTask,
      primaSettingsInProgressTaskResult,
      ratePreferenceTaskResult,
      saveAndUpdatePreferredRateSettings:
        this.saveAndUpdatePreferredRateSettingsTask,
    };

    return model;
  }

  resetController(
    controller: HotelSettingsPreferredRateController,
    isExisting: boolean,
  ): void {
    if (isExisting) {
      // cancel all tasks
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.launchRatePreferenceSimulateRequestTask.cancelAll();
      // Reset controller variables
      controller.updateHasUserChangedPreferredRateSettingsValue(false);
    }
  }

  @action
  willTransition(transition: TransitionWithIsAborted): void {
    /* eslint-disable ember/no-controller-access-in-routes */
    const hasChanges = (
      this.controllerFor(
        'hotel.settings.preferredRate',
      ) as HotelSettingsPreferredRateController
    ).hasUserChangedPreferredRateSettingsValue;
    /* eslint-enable ember/no-controller-access-in-routes */

    if (!hasChanges || transition.isAborted || this.leaveWithoutSaving) {
      return;
    }

    transition.abort();
    void this.confirmLeaveTask.perform(transition);
  }
}
